import React from 'react'
import dayjs from 'dayjs'

import { shortenVersion } from '../../utils/deviceLabels'
import {
	ComputerDesktopIcon,
	DevicePhoneMobileIcon,
	GlobeAltIcon,
	PuzzlePieceIcon,
	XMarkIcon,
	ClockIcon,
} from '@heroicons/react/24/outline'

// Format company name: maz_express_llc -> MAZ EXPRESS LLC
const formatCompanyName = companyName => {
	if (!companyName || companyName === 'Unknown') return null
	return companyName.replace(/_/g, ' ').toUpperCase()
}

/** What the device is called in the list, and in the sign-out confirmation. */
const deviceTitle = (device, deviceType) => {
	const type = deviceType || device.deviceType
	// An extension session always shows its company, even the stored 'Unknown'.
	const company =
		type === 'extension'
			? formatCompanyName(device.companyName) || 'Unknown company'
			: null
	return company || device.model || shortenVersion(device.browser) || 'this device'
}

/**
 * The phone layout: one row per device, details on their own lines underneath
 * so nothing wraps mid-word. The list a device came from is authoritative for
 * its type, so the card does not depend on the record carrying deviceType.
 */
const CompactDeviceCard = ({
	device,
	deviceType,
	isCurrent,
	isDarkMode,
	onRemove,
	isRemoving,
}) => {
	const type = deviceType || device.deviceType
	const isExtension = type === 'extension'
	const Icon = isExtension
		? PuzzlePieceIcon
		: type === 'mobile'
			? DevicePhoneMobileIcon
			: ComputerDesktopIcon

	const company = isExtension
		? formatCompanyName(device.companyName) || 'Unknown company'
		: null
	const title = deviceTitle(device, type)

	const place = [device.city, device.country]
		.filter(part => part && part !== 'Unknown')
		.join(', ')

	// Beside the icon and the Remove button a phone row leaves roughly 190px of
	// text, so each line has to stay short or it wraps and the card grows to
	// five stacked lines. Sign-in method is the one detail that does not earn
	// its place here; it is on the Security screen.
	const lines = [
		[
			company && shortenVersion(device.browser),
			isExtension && device.extensionVersion && `v${device.extensionVersion}`,
			place,
		],
		[device.os, isCurrent ? 'Active now' : dayjs(device.lastCheckIn).fromNow()],
	]
		.map(parts => parts.filter(Boolean).join(' · '))
		.filter(Boolean)

	return (
		<div
			className={`flex items-start gap-3 rounded-lg border px-3 py-3 ${
				isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
			}`}
		>
			<span
				className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${
					isCurrent
						? 'bg-green-500/15 text-green-500'
						: isDarkMode
							? 'bg-gray-600 text-gray-300'
							: 'bg-gray-100 text-gray-500'
				}`}
			>
				<Icon className="h-5 w-5" aria-hidden="true" />
			</span>

			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span
						className={`truncate text-sm font-semibold ${
							isDarkMode ? 'text-white' : 'text-gray-900'
						}`}
					>
						{title}
					</span>
					{isCurrent && (
						<span className="flex-none rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-600">
							Current
						</span>
					)}
				</div>
				{lines.map(line => (
					<p
						key={line}
						className={`text-xs leading-5 ${
							isDarkMode ? 'text-gray-400' : 'text-gray-500'
						}`}
					>
						{line}
					</p>
				))}
			</div>

			{!isCurrent && (
				<button
					type="button"
					onClick={onRemove}
					disabled={isRemoving}
					aria-label={`Remove ${title}`}
					className={`flex min-h-[36px] flex-none items-center gap-1 self-start rounded-lg border px-2 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
						isDarkMode
							? 'border-red-400/30 text-red-300 hover:bg-red-500/20'
							: 'border-red-200 text-red-600 hover:bg-red-50'
					}`}
				>
					{isRemoving ? (
						<span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
					) : (
						<>
							<XMarkIcon className="h-3 w-3" aria-hidden="true" />
							Remove
						</>
					)}
				</button>
			)}
		</div>
	)
}

const DeviceCard = ({
	device,
	deviceType,
	compact,
	isCurrent,
	isDarkMode,
	onRemove,
	isRemoving,
}) => {
	if (compact) {
		return (
			<CompactDeviceCard
				device={device}
				deviceType={deviceType}
				isCurrent={isCurrent}
				isDarkMode={isDarkMode}
				onRemove={onRemove}
				isRemoving={isRemoving}
			/>
		)
	}

	const lastCheckIn = dayjs(device.lastCheckIn).fromNow()
	const isExtension = device.deviceType === 'extension'

	return (
		<div
			className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${
				isDarkMode ? 'border-gray-600' : 'border-gray-200'
			} rounded-lg px-4 py-4`}
		>
			<div className="flex items-center justify-between">
				<div className="space-y-1">
					<div className="flex items-center gap-2">
						<GlobeAltIcon className="h-5 w-5" />
						<span
							className={`font-medium text-${
								isDarkMode ? 'white' : 'gray-900'
							}`}
						>
							{isExtension && formatCompanyName(device.companyName)
								? formatCompanyName(device.companyName)
								: device.browser}
						</span>
						{isExtension && (
							<span
								className={`px-1.5 py-0.5 text-xs rounded-full ${
									isDarkMode
										? 'bg-blue-500/20 text-blue-400'
										: 'bg-blue-100 text-blue-700'
								}`}
							>
								Extension
							</span>
						)}
						{device.loginMethod && (
							<span
								className={`px-1.5 py-0.5 text-xs rounded-full ${
									device.loginMethod === 'google'
										? isDarkMode
											? 'bg-orange-500/20 text-orange-400'
											: 'bg-orange-100 text-orange-700'
										: isDarkMode
											? 'bg-gray-500/20 text-gray-400'
											: 'bg-gray-100 text-gray-600'
								}`}
							>
								{device.loginMethod === 'google' ? 'Google Sign-In' : 'Email / Password'}
							</span>
						)}
						<span
							className={`px-2 py-0.5 ${
								isCurrent ? 'text-sm font-medium' : 'text-xs'
							} rounded-full ${
								isCurrent
									? 'bg-green-500/15 text-green-500'
									: `${
											isDarkMode
												? 'bg-gray-600 text-gray-300'
												: 'bg-gray-200 text-gray-700'
									  }`
							} flex items-center gap-1`}
						>
							{isCurrent ? (
								'Current'
							) : (
								<>
									<ClockIcon className="h-3 w-3" />
									{lastCheckIn}
								</>
							)}
						</span>
					</div>
					<div
						className={`flex items-center gap-2 text-sm text-${
							isDarkMode ? 'gray-300' : 'gray-600'
						}`}
					>
						<ComputerDesktopIcon className="h-4 w-4" />
						<span>
							{device.os}
							{(device.city || device.country) && ' • '}
							{device.city && device.country ? `${device.city}, ${device.country}` : (device.city || device.country)}
							{isExtension && device.extensionVersion && (
								<span className={`text-${isDarkMode ? 'gray-400' : 'gray-500'}`}>
									{' '}• Extension v{device.extensionVersion} • {device.browser}
								</span>
							)}
						</span>
					</div>
				</div>
				{!isCurrent && (
					<button
						onClick={onRemove}
						disabled={isRemoving}
						className={`px-2 py-1 rounded text-xs ${
							isDarkMode
								? 'bg-red-200/10 text-red-300 hover:bg-red-500/20'
								: 'bg-red-50 text-red-600 hover:bg-red-100'
						} transition-colors`}
					>
						{isRemoving ? (
							<div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
						) : (
							<span className="flex items-center gap-1">
								<XMarkIcon className="h-3 w-3" />
								Remove
							</span>
						)}
						<span className="sr-only">Remove device</span>
					</button>
				)}
			</div>
		</div>
	)
}

export default DeviceCard
